import {createAsyncThunk} from "@reduxjs/toolkit";
import {HOSTNAME} from "../Configs/Domain";
import {getValuesByPaths, prepareJsonPayload} from "../Store/utils";
import {deepObjectFromDotPath} from "../utils/tools";


const apiRequest = async (url, method, data) => {
  const response = await fetch(url, prepareJsonPayload({ method, data }));
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || `Failed to ${method} data`);
  }
  return response.json();
};

// Error handling helper function
const handleError = (error, rejectWithValue) => {
  return rejectWithValue(error.message);
};

export const deleteUserAppData = createAsyncThunk(
  'appData/deleteUserAppData',
  async (ids, { rejectWithValue }) => {
    try {
      await apiRequest(`${HOSTNAME}/user/app_data/delete`, "DELETE", ids);
      return ids;
    } catch (error) {
      return handleError(error, rejectWithValue);
    }
  }
);

export const saveUserAppData = createAsyncThunk(
  'user/saveUserAppData',
  async ({ appType, title, data, id }, { getState, rejectWithValue }) => {
    try {
      const url = id ? `${HOSTNAME}/user/app_data/${id}/update` : `${HOSTNAME}/user/app_data/create`;
      const body = id ? { data } : { app_type: appType, title, data };
      const responseData = await apiRequest(url, id ? "PUT" : "POST", body);
      return responseData;
    } catch (error) {
      return handleError(error, rejectWithValue);
    }
  }
);


export const updateUser = createAsyncThunk(
  'user/updateUser',
  async ({ updateData, match }, { getState, rejectWithValue }) => {
      const userId = localStorage.getItem('uid');

      const payload = {
        match: match ? match : {uid: userId},
        update: updateData
      }
      try {
          const response = await fetch(`${HOSTNAME}/user/update`, prepareJsonPayload({data: payload}));

          if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update user data');
          }

        const responseData = await response.json();
        return {responseData, updateData}; // This is the updated user data from the server
        } catch (error) {
        return rejectWithValue(error.message);
        }
    });


/**
 * Specific implementation of updateUser specific to setting and returning data that may be dotpath nested etc
 * and being able to correctly update this within the redux state
 */
export const setUserFields = createAsyncThunk(
  'user/setUserData',
  async ({ changedFields, match }, { getState, rejectWithValue }) => {
      const userId = localStorage.getItem('uid');

      const payload = {
        match: match ? match : {uid: userId},
        update: {
            $set: changedFields
        }
      }
      try {
          const response = await fetch(`${HOSTNAME}/user/update`, prepareJsonPayload({data: payload}));
          if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update user data');
          }
          const responseData = await response.json();
          return deepObjectFromDotPath(changedFields); // This is the updated user data from the server
        } catch (error) {
        return rejectWithValue(error.message);
        }
    });

export const fetchUserFields = createAsyncThunk(
  'user/fetchUserFields',
  async (keyPaths, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const existingValues = getValuesByPaths(state.user.data, keyPaths);
      const missingKeys = keyPaths.filter((key, index) => !existingValues[index]);

      if (missingKeys.length === 0) {
        // All fields exist, no need to fetch anything
        return;
      }

      const response = await fetch(`${HOSTNAME}/user/fetch?fields=${missingKeys.join(',')}`, prepareJsonPayload({
        method: "GET"
      }));

      if (!response.ok) {
        throw new Error('Failed to fetch user fields');
      }

      const responseData = await response.json();
      return responseData; // Should match the structure of the missing fields
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const createProspect = createAsyncThunk(
  'user/createProspect',
  async ({ name, email, phone }, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await fetch(`${HOSTNAME}/prospects/create`, prepareJsonPayload({
        data: { name, email, phone },
        method: "POST"
      }));

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create prospect');
      }

      const prospectData = await response.json(); // Assuming this includes the new prospect's userId
      return prospectData; // Return the new prospect data for further use
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProspectPortfolios = createAsyncThunk(
    'user/updateProspectPortfolios',
    async ({ prospectUserId, portfolios }, { getState, rejectWithValue }) => {
        try {
            const response = await fetch(`${HOSTNAME}/prospects/${prospectUserId}/portfolios/update`,
                prepareJsonPayload({data: portfolios, method: "PUT"}))
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update prospect portfolios');
            }

            const data = await response.json();
            return { prospectUserId, portfolios: data.portfolios };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);



export const createPage = createAsyncThunk(
  'user/createPage',
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${HOSTNAME}/pages/create`, prepareJsonPayload({
        data,
        method: 'POST',
      }));

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create page');
      }

      const pageData = await response.json();
      return pageData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePage = createAsyncThunk(
  'user/updatePage',
  async ({ pageId, data }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${HOSTNAME}/pages/update/${pageId}`, prepareJsonPayload({
        data: data,
        method: 'PUT',
      }));

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update page');
      }

      const pageData = await response.json();
      return {pageId, pageData};
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const deletePages = createAsyncThunk(
  'user/deletePages',
  async ({ pageIds }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${HOSTNAME}/pages/delete`, prepareJsonPayload({
        method: 'DELETE',
        data: pageIds,
      }));

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete pages');
      }
      return pageIds;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);



export const resetPassword = createAsyncThunk(
    'user/resetPassword',
    async ({ password }, { rejectWithValue }) => {
        try {
        const response = await fetch(`${HOSTNAME}/user/reset/password`, prepareJsonPayload({
            data: { password },
            method: 'POST',
        }));

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Failed to reset password');
        }

        return;
        } catch (error) {
        return rejectWithValue(error.message);
        }
    }

)


export const createTouchpoint = createAsyncThunk(
  'touchpoint/create',
  async ({ data }, { rejectWithValue }) => {
    try {
      const touchpointData = await apiRequest(`${HOSTNAME}/touchpoints/create`, "POST", data);
      return touchpointData;
    } catch (error) {
      return handleError(error, { rejectWithValue });
    }
  }
);

export const updateTouchpoint = createAsyncThunk(
  'touchpoint/update',
  async ({ touchpointId, data }, { rejectWithValue }) => {
    try {
      const touchpointData = await apiRequest(
        `${HOSTNAME}/touchpoints/update/${touchpointId}`,
        "PUT",
        data
      );
      return { touchpointId, touchpointData };
    } catch (error) {
      return handleError(error, { rejectWithValue });
    }
  }
);

export const deleteTouchpoint = createAsyncThunk(
  'touchpoint/delete',
  async ({ touchpointId }, { rejectWithValue }) => {
    try {
      await apiRequest(`${HOSTNAME}/touchpoints/delete/${touchpointId}`, "DELETE");
      return touchpointId;
    } catch (error) {
      return handleError(error, { rejectWithValue });
    }
  }
);

export const fetchTouchpoint = createAsyncThunk(
  'touchpoint/fetch',
  async (touchpointId , { rejectWithValue }) => {
    try {
      const touchpoint = await apiRequest(
        `${HOSTNAME}/touchpoints/fetch/${touchpointId}`,
        "GET"
      );
      console.log(touchpoint)
      return touchpoint;
    } catch (error) {
      return handleError(error, { rejectWithValue });
    }
  }
);

export const fetchTouchpoints = createAsyncThunk(
  'touchpoint/fetchTouchpoints',
  async (touchpointIds , { rejectWithValue }) => {
    try {
      const touchpoints = await apiRequest(
        `${HOSTNAME}/touchpoints/fetch/many?ids=${touchpointIds.join(',')}`,
        "GET"
      );
      return touchpoints;
    } catch (error) {
      return handleError(error, { rejectWithValue });
    }
  }
);